import { Injectable } from "@angular/core";
import { formatDate } from "@angular/common";
import { DomSanitizer, Title } from "@angular/platform-browser";
import { map, Observable, tap } from "rxjs";
import { PdvRepository } from "../repositories/pdv.repository";
import { CompanyModel } from "../models/company.model";
import { PdvSettingsModel } from "../models/pdv-settings.model";
import { EventModel } from "../models/event.model";
import { PRIVACY_POLICY } from "../data/privacy-policy";
import { HALF_PRICE_POLICY } from "../data/half-privacy-policy";
import { TERMS_OF_USE } from "../data/terms-of-use";
import { environment } from "src/environments/environment";

export interface LoadSiteResponse {
	settings: PdvSettingsModel;
	company: CompanyModel;
	events: EventModel[];
}

@Injectable({
	providedIn: "root",
})
export class PdvService {
	constructor(
		private pdvRepository: PdvRepository,
		private sanitizer: DomSanitizer,
		private titleService: Title
	) { }

	load(): Observable<LoadSiteResponse> {
		return this.pdvRepository.loadSite().pipe(

			map((res) => {

				return <LoadSiteResponse>{
					company: {
						id: res?.pdv?.company?.id ?? "",
						name: res?.pdv?.company?.name ?? "",
						site: res?.pdv?.company?.site,
						domain: res?.pdv?.company?.domain,
						recaptchaPublicKey: res?.pdv?.company?.recaptchaPublicKey,
						images: res?.pdv?.company?.images,
						primaryColor: res?.pdv?.company?.primaryColor,
						secondaryColor: res?.pdv?.company?.secondaryColor,
						seatsioWorkspaceKey: res?.pdv?.company?.seatsioWorkspaceKey,
						useSeatsIo: res?.pdv?.company?.useSeatsIo,
						limitByCPF: res?.pdv?.company?.limitPerCPF,
						termsOfUse: TERMS_OF_USE,
						googleAnalyticsKey: res?.pdv?.company?.googleAnalyticsKey,
						aboutUs: res?.pdv?.id == "748f1e20-a20d-41c4-97c9-06253fd4adec" ? res?.pdv?.company.aboutUs.split("IngressoSA").join('Goianão') : res?.pdv?.company?.aboutUs,
						policy: res?.pdv?.company?.policy != null ? res?.pdv?.company?.policy : PRIVACY_POLICY,
						halfPricePolicy: HALF_PRICE_POLICY,
					},
					settings: {
						socialNetworks: res?.pdv?.company?.socialNetworks,
						name: res?.pdv?.configs?.name ?? "",
						email: res?.pdv?.configs?.email,
						company: res?.pdv?.configs?.company,
						xAccessToken: res?.pdv?.configs?.xAccessToken,
						keyMercadoPago: res?.pdv?.configs?.keyMercadoPago,
						urlios: res?.pdv?.configs?.urlios,
						urlAndroid: res?.pdv?.configs?.urlAndroid,
						seatsioWorkspaceKey: res?.pdv?.configs?.seatsioWorkspaceKey,
						socialMedia: res?.pdv?.configs?.socialMedia,
						colors: res?.pdv?.configs?.colors,
					},
					events: (res?.events ?? []).map((event: any) => {
						const dates: string[] = event?.dates.map(
							(x: any) => x.dateStartEvent
						);

						const startDate = dates?.[0]
							? formatDate(dates[0], "EEEE, d 'de' MMMM", "pt-BR", "+0000")
							: undefined;

						const startTime = dates?.[0]
							? formatDate(dates[0], "HH:mm", "pt-BR", "+0000")
							: undefined;

						const slugs =
							event?.slugs
								?.filter((item: any) => item?.slug)
								?.map((item: any) => ({ slug: item.slug })) ?? undefined;

						const tags =
							event?.tags
								?.filter((item: any) => item?.tag)
								?.map((item: any) => ({ tag: item.tag, logo: item.logo })) ?? undefined;

						const categories =
							event?.categories
								?.filter((item: any) => item?.name)
								?.map((item: any) => item.name) ?? undefined;

						const googleMapsUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
							encodeURI(
								`https://maps.google.com/maps?q=${event?.address}&z=15&ie=UTF8&iwloc=&output=embed`
							)
						);

						const sectors = event?.sectors?.map((sector: any) => {
							return {
								id: sector.id,
								name: sector.name,
								position: sector.position,
								useMapPlace: sector.useMapPlace,
								useSeats: sector.useSeats,
							};
						});

						const tickets = event?.sectors?.flatMap((sector: any) =>
							sector?.tickets?.map((ticket: any) => {
								return {
									id: ticket.id,
									ticketDateId: ticket.dateTickets[0].id,
									lotId: ticket.dateTickets[0].lotCurrent.id,
									fees: ticket.dateTickets[0].fee,
									sectorId: sector.id,
									name: ticket.name,
									fengPlansExtra: ticket.fengPlansExtra,
									fengPlansInvited: ticket.fengPlansInvited,
									fengPlansPartner: ticket.fengPlansPartner,
									gender: ticket.gender,
									type: ticket.type,
									description: ticket.description,
									position: ticket.position,
									batch: ticket.dateTickets[0].lotCurrent.name,
									price: ticket.dateTickets[0].lotCurrent.prices[0].price,
									fee:
										ticket.dateTickets[0].lotCurrent.prices[0].priceVirtual -
										ticket.dateTickets[0].lotCurrent.prices[0].price,
									qty: 0,
									blacklist: ticket.blacklist,
									useFacialRecognition: ticket.useFacialRecognition,
									shouldHideTypeOnFrontend: ticket.shouldHideTypeOnFrontend
								};
							})
						);

						let tagsFilter = ""
						if (event.tagsFilter != null) {
							tagsFilter = JSON.parse(event.tagsFilter)
						}

						return {
							id: event?.id ?? "",
							name: event?.name ?? "",
							banner: event?.banner,
							address: event?.address ?? "",
							instagram: event?.instagram,
							city: event?.city?.name ?? "",
							state: event?.city?.state?.name ?? "",
							uf: event?.city?.state?.uf ?? "",
							dates: dates,
							hideFullTickets: event?.hideFullTickets,
							description: event?.description,
							fengIntegration: event?.fengIntegration,
							startDate: startDate,
							startTime: startTime,
							limitCPF: event?.company?.limitPerCPF,
							limitByCPF: event?.limitByCPF,
							fbPixel: event?.fbPixel,
							redirectToExternalLink: event?.redirectToExternalLink,
							requireCustomerDataOnPdv: event?.requireCustomerDataOnPdv,
							skipCustomerDataOnPdv: event?.skipCustomerDataOnPdv,
							slugs: slugs,
							tags: tags,
							tagsFilter: tagsFilter,
							categories: categories,
							ticketTags: event?.ticketTags ?? undefined,
							googleMapsUrl: googleMapsUrl,
							sectors: sectors,
							tickets: tickets,
							seatsioEventKey: event?.seatsioEventKey ?? undefined,
							seatsioMapKey: event?.seatsioMapKey ?? undefined,
							useGoogleEvents: event?.useGoogleEvents,
							paymentMethods: event?.paymentMethods,
							allowMapV2: event?.allowMapV2,
							imageWeb: event?.imageWeb ?? undefined,
							imagePDV: event?.imagePDV ?? undefined,
							company: event?.company,
							facialOnboardingProvider: event?.facialOnboardingProvider,
						};
					}),
				};
			}),
			map((pdv) => {
				const events = pdv.events.sort((a, b) => {
					const dateA = new Date(a?.dates?.[0]);
					const dateB = new Date(b?.dates?.[0]);
					return dateA.getTime() - dateB.getTime();
				});
				return { ...pdv, events };
			}),
			tap((pdv) => {
				this.titleService.setTitle(environment.title);
			})
		);
	}
}
